/**
 * Custom Tiptap Node extensions that let admins insert callouts, decorated
 * dividers, and inline SVG shapes from the "Shapes" toolbar.
 *
 * All three round-trip through Tiptap's schema as plain HTML, so `getHTML()`
 * emits exactly the markup we render on the public site — no bespoke
 * serialisation glue needed downstream. Styling lives in App.css under
 * `.slh-callout*`, `hr.slh-hr-*`, and `.slh-shape`.
 */
import { Node, mergeAttributes } from "@tiptap/core";
import { sanitizeHtml } from "@/components/ui/RichHtml";

// Short, collision-safe id for matching a specific HtmlEmbed node back to
// its on-screen DOM element — needed because the raw pasted HTML can be
// rewritten by the browser on render, so comparing HTML strings (as the
// image/video nodes do via `src`) is unreliable for this node.
export const genEmbedId = () => `he_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

// ---- Callout (block) -----------------------------------------------------
// Simple block-level container that wraps a paragraph (or several).
// Content: `block+` so admins can drop headings, lists, images inside.
export const Callout = Node.create({
  name: "callout",
  group: "block",
  content: "block+",
  defining: true,

  addAttributes() {
    return {
      variant: {
        default: "info",
        parseHTML: (el) => {
          const m = /slh-callout-(\w+)/.exec(el.className || "");
          return m ? m[1] : "info";
        },
        renderHTML: (attrs) => ({
          class: `slh-callout slh-callout-${attrs.variant}`,
        }),
      },
      icon: {
        default: "ℹ️",
        parseHTML: (el) => el.querySelector(".slh-callout-icon")?.textContent?.trim() || "ℹ️",
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [{ tag: "div.slh-callout", contentElement: ".slh-callout-body", getAttrs: (el) => ({ variant: /slh-callout-(\w+)/.exec(el.className || "")?.[1] || "info" }) }];
  },

  renderHTML({ HTMLAttributes, node }) {
    const icon = node.attrs.icon || "ℹ️";
    return [
      "div",
      mergeAttributes(HTMLAttributes, { class: `slh-callout slh-callout-${node.attrs.variant}` }),
      ["span", { class: "slh-callout-icon", contenteditable: "false" }, icon],
      ["div", { class: "slh-callout-body" }, 0],
    ];
  },
});

// ---- Fancy divider (block) ----------------------------------------------
// Standalone <hr> variants — extends the concept of the built-in
// HorizontalRule with a `variant` attribute so decorated dividers survive
// serialise → parse → serialise cycles.
export const FancyDivider = Node.create({
  name: "fancyDivider",
  group: "block",
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      variant: {
        default: "dotted",
        parseHTML: (el) => {
          const m = /slh-hr-(\w+)/.exec(el.className || "");
          return m ? m[1] : "dotted";
        },
        renderHTML: (attrs) => ({ class: `slh-hr-${attrs.variant}` }),
      },
    };
  },

  parseHTML() {
    // Higher priority than StarterKit's default HorizontalRule ("hr", 50)
    // so a saved `<hr class="slh-hr-dotted">` parses back into this node
    // (preserving its variant) instead of being swallowed as a plain hr.
    return [{ tag: "hr[class*='slh-hr-']", priority: 60 }];
  },

  renderHTML({ HTMLAttributes, node }) {
    return ["hr", mergeAttributes(HTMLAttributes, { class: `slh-hr-${node.attrs.variant}` })];
  },
});

// ---- Inline SVG shape ----------------------------------------------------
// Atom inline node with a single `shape` attribute. Both the editor and
// the public site look up the actual SVG from the same shared map, so
// nothing has to survive Tiptap's schema besides the shape id.
export const SHAPE_SVGS = {
  rectangle: `<svg width="48" height="32" viewBox="0 0 48 32" xmlns="http://www.w3.org/2000/svg"><rect x="2" y="2" width="44" height="28" rx="3" fill="#e0e7ff" stroke="#4338ca" stroke-width="2"/></svg>`,
  circle:    `<svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"><circle cx="16" cy="16" r="14" fill="#dcfce7" stroke="#15803d" stroke-width="2"/></svg>`,
  arrow:     `<svg width="56" height="20" viewBox="0 0 56 20" xmlns="http://www.w3.org/2000/svg"><line x1="4" y1="10" x2="46" y2="10" stroke="#0f766e" stroke-width="2.5" stroke-linecap="round"/><polyline points="40,4 50,10 40,16" fill="none" stroke="#0f766e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  line:      `<svg width="56" height="8" viewBox="0 0 56 8" xmlns="http://www.w3.org/2000/svg"><line x1="2" y1="4" x2="54" y2="4" stroke="#525252" stroke-width="2" stroke-linecap="round"/></svg>`,
  star:      `<svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"><polygon points="16,3 20,12 30,13 22,20 24,30 16,25 8,30 10,20 2,13 12,12" fill="#fef3c7" stroke="#b45309" stroke-width="1.5" stroke-linejoin="round"/></svg>`,
  diamond:   `<svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"><polygon points="16,3 29,16 16,29 3,16" fill="#fce7f3" stroke="#be185d" stroke-width="2" stroke-linejoin="round"/></svg>`,
};

// ---- Video embed (block, resizable + floatable) --------------------------
// Wraps a YouTube/Vimeo/generic iframe embed so admins can resize it (as a
// % width) and float it left/right so body text wraps beside it, exactly
// like the image-resize workflow. Also parses the legacy
// `@tiptap/extension-youtube` output (`div[data-youtube-video]`) so
// previously-saved YouTube embeds upgrade to this node on next load.
export function getVideoEmbedSrc(url) {
  const yt = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/.exec(url || "");
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = /vimeo\.com\/(?:video\/)?(\d+)/.exec(url || "");
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return url || "";
}

export const VideoEmbed = Node.create({
  name: "videoEmbed",
  group: "block",
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      src: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-video-src") || el.querySelector("iframe,video")?.getAttribute("src") || "",
        renderHTML: () => ({}),
      },
      kind: {
        default: "iframe",
        parseHTML: (el) => el.getAttribute("data-video-kind") || (el.querySelector("video") ? "file" : "iframe"),
        renderHTML: () => ({}),
      },
      width: {
        default: "100",
        parseHTML: (el) => (el.style.width ? el.style.width.replace("%", "").trim() : "100"),
        renderHTML: () => ({}),
      },
      float: {
        default: "none",
        parseHTML: (el) => /video-float-(\w+)/.exec(el.className || "")?.[1] || "none",
        renderHTML: () => ({}),
      },
      display: {
        default: "inline",
        parseHTML: (el) => (el.classList.contains("video-embed-modal") ? "modal" : "inline"),
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [{ tag: "div.video-embed" }, { tag: "div[data-youtube-video]" }];
  },

  renderHTML({ node }) {
    const { src, kind, width, float, display } = node.attrs;
    const wrapper = document.createElement("div");
    const classes = ["video-embed"];
    if (float && float !== "none") classes.push(`video-float-${float}`);
    if (display === "modal") classes.push("video-embed-modal");
    wrapper.className = classes.join(" ");
    wrapper.setAttribute("data-video-embed", "");
    wrapper.setAttribute("data-video-src", src || "");
    wrapper.setAttribute("data-video-kind", kind || "iframe");
    wrapper.style.width = `${width || "100"}%`;
    const inner = document.createElement("div");
    inner.className = "video-embed-inner";

    if (display === "modal") {
      // Thumbnail + play button — the actual player only mounts in a
      // lightbox when clicked (see RichHtml.jsx on the public site).
      const yt = /youtube\.com\/embed\/([^?&]+)/.exec(src || "");
      if (yt) {
        const img = document.createElement("img");
        img.setAttribute("src", `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg`);
        img.setAttribute("alt", "Video thumbnail");
        img.className = "video-embed-thumb";
        inner.appendChild(img);
      } else if (kind === "file") {
        const video = document.createElement("video");
        video.setAttribute("src", src || "");
        video.setAttribute("muted", "true");
        video.setAttribute("preload", "metadata");
        inner.appendChild(video);
      }
      const play = document.createElement("div");
      play.className = "video-embed-play";
      play.innerHTML = '<svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28"><path d="M8 5v14l11-7z"/></svg>';
      inner.appendChild(play);
    } else if (kind === "file") {
      const video = document.createElement("video");
      video.setAttribute("src", src || "");
      video.setAttribute("controls", "true");
      video.setAttribute("playsinline", "true");
      video.setAttribute("preload", "metadata");
      inner.appendChild(video);
    } else {
      const iframe = document.createElement("iframe");
      iframe.setAttribute("src", src || "");
      iframe.setAttribute("frameborder", "0");
      iframe.setAttribute("loading", "lazy");
      iframe.setAttribute(
        "allow",
        "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share",
      );
      iframe.setAttribute("allowfullscreen", "true");
      inner.appendChild(iframe);
    }
    wrapper.appendChild(inner);
    return wrapper;
  },
});

// ---- Raw HTML embed (block, resizable + floatable) -----------------------
// Lets admins drop in arbitrary embed code (Google Maps iframe, review
// widgets, custom markup) the same way they embed a video — resizable as a
// % width and float-able left/right. The raw markup is stored as the
// `.html-embed-inner`'s innerHTML so it round-trips through getHTML()
// exactly as authored. Public rendering still goes through RichHtml's
// DOMPurify pass (scripts/event handlers stripped there, same as every
// other rich-HTML surface) — this node only owns layout/position.
export const HtmlEmbed = Node.create({
  name: "htmlEmbed",
  group: "block",
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      html: {
        default: "",
        parseHTML: (el) => el.querySelector(".html-embed-inner")?.innerHTML || "",
        renderHTML: () => ({}),
      },
      width: {
        default: "100",
        parseHTML: (el) => (el.style.width ? el.style.width.replace("%", "").trim() : "100"),
        renderHTML: () => ({}),
      },
      float: {
        default: "none",
        parseHTML: (el) => /html-embed-float-(\w+)/.exec(el.className || "")?.[1] || "none",
        renderHTML: () => ({}),
      },
      embedId: {
        default: null,
        // Backfill a fresh id for embeds saved before this attribute
        // existed, so Apply / Settings2 / drag-resize can still find them.
        parseHTML: (el) => el.getAttribute("data-embed-id") || genEmbedId(),
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [{ tag: "div.html-embed" }];
  },

  renderHTML({ node }) {
    const { html, width, float, embedId } = node.attrs;
    const wrapper = document.createElement("div");
    const classes = ["html-embed"];
    if (float && float !== "none") classes.push(`html-embed-float-${float}`);
    wrapper.className = classes.join(" ");
    wrapper.setAttribute("data-html-embed", "");
    if (embedId) wrapper.setAttribute("data-embed-id", embedId);
    wrapper.style.width = `${width || "100"}%`;
    const inner = document.createElement("div");
    inner.className = "html-embed-inner";
    // Sanitised even inside the admin editor — a pasted embed with an
    // `onerror`/`onload` handler must not get to execute just because an
    // admin opened the page; the public site already enforces this via
    // RichHtml, this keeps the editor to the same standard.
    inner.innerHTML = sanitizeHtml(html || "");
    wrapper.appendChild(inner);
    return wrapper;
  },
});

// ---- CTA button (block) ---------------------------------------------------
// A standalone call-to-action button: configurable copy, destination URL,
// and click target (same tab / new tab / in-page modal). Renders as plain
// HTML (`div.slh-cta-button > a`) so it round-trips through getHTML() like
// every other node here, and so the public site (RichHtml) can intercept
// `.slh-cta-modal` clicks to open the destination in a lightbox instead of
// navigating away — same pattern as the video-embed "modal" play button.
export const CtaButton = Node.create({
  name: "ctaButton",
  group: "block",
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      text: {
        default: "Learn More",
        parseHTML: (el) => el.querySelector("a")?.textContent?.trim() || "Learn More",
        renderHTML: () => ({}),
      },
      url: {
        default: "",
        parseHTML: (el) => el.querySelector("a")?.getAttribute("href") || "",
        renderHTML: () => ({}),
      },
      target: {
        default: "same",
        parseHTML: (el) => {
          const attr = el.getAttribute("data-cta-target");
          if (attr === "new" || attr === "modal" || attr === "same") return attr;
          return el.querySelector("a")?.getAttribute("target") === "_blank" ? "new" : "same";
        },
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [{ tag: "div.slh-cta-button" }];
  },

  renderHTML({ node }) {
    const { text, url, target } = node.attrs;
    const wrapper = document.createElement("div");
    wrapper.className = "slh-cta-button";
    wrapper.setAttribute("data-cta-target", target || "same");
    wrapper.setAttribute("contenteditable", "false");
    const anchor = document.createElement("a");
    anchor.className = "slh-cta-button-anchor no-global-style";
    anchor.setAttribute("href", url || "#");
    if (target === "new") {
      anchor.setAttribute("target", "_blank");
      anchor.setAttribute("rel", "noopener noreferrer");
    } else if (target === "modal") {
      anchor.classList.add("slh-cta-modal");
      anchor.setAttribute("data-cta-modal", "true");
      anchor.setAttribute("data-cta-url", url || "");
    }
    anchor.textContent = text || "Learn More";
    wrapper.appendChild(anchor);
    return wrapper;
  },
});

export const InlineShape = Node.create({
  name: "inlineShape",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      shape: {
        default: "rectangle",
        parseHTML: (el) => el.getAttribute("data-shape") || "rectangle",
        renderHTML: (attrs) => ({ "data-shape": attrs.shape }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "span.slh-shape" }];
  },

  renderHTML({ HTMLAttributes, node }) {
    // Build a real DOM node so we can inject the SVG safely via innerHTML.
    // Called by both the editor and `getHTML()` — the resulting outerHTML
    // is exactly what lands in the saved config and re-parses back into a
    // faithful InlineShape on the next mount.
    const span = document.createElement("span");
    span.className = "slh-shape";
    span.setAttribute("data-shape", node.attrs.shape);
    Object.entries(HTMLAttributes || {}).forEach(([k, v]) => {
      if (v != null && !span.hasAttribute(k)) span.setAttribute(k, String(v));
    });
    span.innerHTML = SHAPE_SVGS[node.attrs.shape] || SHAPE_SVGS.rectangle;
    return span;
  },
});
